import { FC, useEffect } from 'react';

// Third-party
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CardHeader from '@mui/material/CardHeader';
import SvgIcon from '@mui/material/SvgIcon';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';

// Redux-toolkit
import { useTheme } from '@mui/material';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseTooltip } from '@mgm/common/components/base';
import {
  getFormattedSubmissions,
  getSelectedSubmission,
} from '../../../../store/tasklists/selectors';
import { tasklistsActions } from '../../../../store/tasklists/slice';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';

// Components
import { SubmissionNotes } from './SubmissionNotes';
import { LinearProgressWithLabel } from '../../../../components/LinearProgressWithLabel';
import { SubmissionListWrapper } from './SubmissionListWrapper';

// Assets
import { ReactComponent as ViewIcon } from '../../../../static/icons/ic_view.svg';
import { ReactComponent as NoteIcon } from '../../Shared/static/ic_note.svg';

// Hooks
import { TEmptySubmission, TSubmissionTask } from '../../../../types';

type TAllSubmissionsNav = {
  submissionsByFilters: { [p: string]: TEmptySubmission[] }
};

const AllSubmissionsNav: FC<TAllSubmissionsNav> = ({ submissionsByFilters }) => {
  const dispatch = useAppDispatch();
  const formattedSubmissions = useAppSelector(getFormattedSubmissions);
  const selectedSubmission = useAppSelector(getSelectedSubmission);

  const theme = useTheme() as TCustomTheme;

  const { isMobile } = useCustomTheme(theme);

  useEffect(() => {
    dispatch(tasklistsActions.setSelectedSubmission({
      ...formattedSubmissions[0],
      date: Object.keys(submissionsByFilters)[0],
    }));
  }, []);

  const getCompletedTask = (tasks: Array<TSubmissionTask>) => tasks?.filter((task) => task.doneBy.length > 0).length;

  const getProgressValue = (tasks: Array<TSubmissionTask>) => (getCompletedTask(tasks) / (tasks?.length || 0)) * 100;

  const setSelectedSubmissionTemplate = (submission: TEmptySubmission, key: string) => {
    dispatch(tasklistsActions.setSelectedSubmission({ ...submission, date: key }));
    dispatch(tasklistsActions.setIsOpenAllSubmissionsNav(false));
  };

  return (
    <SubmissionListWrapper
      isSubmissionsEmpty={!formattedSubmissions.length}
    >
      <Box maxHeight={600} overflow="auto" paddingY={1}>
        {Object.entries(submissionsByFilters)?.map(([key, value]) => (
          <Box key={key}>
            <Box paddingY={1} justifyContent="center" display="flex">
              <Typography
                textAlign="center"
                color="primary"
                variant="caption"
              >
                {key}
              </Typography>
            </Box>
            {value?.map((submission) => (
              <Card
                sx={{
                  my: 1,
                  bgcolor: 'background.paper',
                  backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                  boxShadow: 'none',
                  ':hover': {
                    background: 'rgba(168, 85, 247, 0.16)',
                    borderRadius: 0,
                  },
                  ...(submission.tasklistId + key
                      === selectedSubmission.tasklistId + selectedSubmission.date && {
                    background: 'rgba(168, 85, 247, 0.16)',
                    borderRadius: 0,
                  }),
                  ...(isMobile && {
                    borderRadius: 0,
                  }),
                }}
                elevation={0}
                component={Paper}
                key={submission.tasklistId + key}
              >
                <CardHeader
                  sx={{
                    py: 0,
                  }}
                  title={(
                    <Stack spacing={2} direction="row">
                      <Typography>{submission.title}</Typography>
                      {submission.notes.length > 0 && (
                      <BaseTooltip tooltipText={(
                        <SubmissionNotes notes={submission.notes} />
                      )}
                      >
                        <SvgIcon
                          className="cursor-pointer"
                          fontSize="small"
                          component={NoteIcon}
                          inheritViewBox
                        />
                      </BaseTooltip>
                      )}
                    </Stack>
                  )}
                  titleTypographyProps={{
                    variant: 'body1',
                  }}
                  action={(
                    <Box marginTop={2} marginRight={2}>
                      <Box marginBottom={1} display="flex" justifyContent="end">
                        <SvgIcon
                          onClick={() => setSelectedSubmissionTemplate(submission, key)}
                          className="cursor-pointer"
                          component={ViewIcon}
                        />
                      </Box>
                    </Box>
                          )}
                />
                <Stack spacing={3} sx={{ px: 3, mb: 2, mt: 1 }}>
                  <LinearProgressWithLabel
                    progress={getProgressValue(submission.tasks)}
                    color={getProgressValue(submission.tasks) < 100 ? 'warning' : 'success'}
                    sx={{ height: 8, bgcolor: 'grey.50016', borderRadius: 8 }}
                  />
                </Stack>
              </Card>
            ))}
          </Box>
        ))}
      </Box>

    </SubmissionListWrapper>
  );
};

export {
  AllSubmissionsNav,
};
